import { useState, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { getCountries, joinCountry, leaveCountry } from '../utils/api';
import { getFlagImageUrl } from '../utils/flags';
import type { Country } from '../types';

// Flag image component with fallback - rectangular to show full flag respectfully
function FlagImage({ code, size = 20, style }: { code: string; size?: number; style?: CSSProperties }) {
  const [error, setError] = useState(false);
  const flagWidth = size * 1.5; // 3:2 aspect ratio
  const flagHeight = size;
  
  if (error) {
    return (
      <span style={{ fontSize: size * 0.5, fontWeight: 600, color: 'white', ...style }}>
        {code}
      </span>
    );
  }
  
  return (
    <img 
      src={getFlagImageUrl(code)} 
      alt={code}
      onError={() => setError(true)}
      style={{ 
        width: flagWidth, 
        height: flagHeight, 
        objectFit: 'cover', 
        borderRadius: '3px',
        ...style 
      }}
    />
  );
}

interface CountryButtonProps {
  onCountryChange?: () => void;
}

export default function CountryButton({ onCountryChange }: CountryButtonProps) {
  const { user, isAuthenticated, refreshUser } = useAuth();
  const [showPicker, setShowPicker] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [loading, setLoading] = useState(false);

  // Fetch countries
  const { data: countriesData } = useQuery({
    queryKey: ['countries'],
    queryFn: getCountries,
    staleTime: 5 * 60 * 1000,
  });

  const countries = countriesData?.countries || [];

  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    if (!countrySearch.trim()) return countries.slice(0, 10);
    const search = countrySearch.toLowerCase();
    return countries.filter(c =>
      c.name.toLowerCase().includes(search) ||
      c.code.toLowerCase().includes(search)
    ).slice(0, 10);
  }, [countries, countrySearch]);

  // Get user's current country
  const userCountry = useMemo(() => {
    if (!user?.countryCode) return null;
    return countries.find(c => c.code === user.countryCode) || null;
  }, [user?.countryCode, countries]);

  const handleCountrySelect = async (country: Country) => {
    try {
      setLoading(true);
      await joinCountry(country.code);
      await refreshUser();
      setShowPicker(false);
      setCountrySearch('');
      onCountryChange?.();
    } catch (error) {
      console.error('Failed to set country:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCountryRemove = async () => {
    try {
      setLoading(true);
      await leaveCountry();
      await refreshUser();
      onCountryChange?.();
    } catch (error) {
      console.error('Failed to remove country:', error);
    } finally {
      setLoading(false);
    }
  };

  // Only show for authenticated users
  if (!isAuthenticated) return null;

  const buttonStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    borderRadius: '12px',
    background: userCountry ? 'rgba(20, 241, 149, 0.1)' : 'rgba(255,255,255,0.05)',
    border: `1px solid ${userCountry ? 'rgba(20, 241, 149, 0.3)' : 'rgba(255,255,255,0.1)'}`,
    cursor: 'pointer',
    transition: 'all 0.2s',
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setShowPicker(!showPicker)}
        style={buttonStyle}
      >
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {userCountry ? <FlagImage code={userCountry.code} size={20} /> : <span style={{ fontSize: '20px' }}>🌍</span>}
        </span>
        <span style={{ 
          fontSize: '12px', 
          color: userCountry ? '#14F195' : 'rgba(255,255,255,0.6)',
          fontWeight: 500,
          maxWidth: '80px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {userCountry ? userCountry.name : 'Country'}
        </span>
        <svg 
          style={{ 
            width: '12px', 
            height: '12px', 
            color: 'rgba(255,255,255,0.4)', 
            transform: showPicker ? 'rotate(180deg)' : 'rotate(0)', 
            transition: 'transform 0.2s' 
          }} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {showPicker && (
        <>
          {/* Backdrop */}
          <div 
            style={{ position: 'fixed', inset: 0, zIndex: 40 }} 
            onClick={() => { setShowPicker(false); setCountrySearch(''); }} 
          />
          
          {/* Dropdown */}
          <div style={{
            position: 'absolute',
            right: 0,
            top: '100%',
            marginTop: '8px',
            width: '260px',
            background: 'rgba(10, 10, 20, 0.98)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '16px',
            overflow: 'hidden',
            zIndex: 50,
            boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
          }}>
            {/* Header */}
            <div style={{ 
              padding: '12px 14px', 
              borderBottom: '1px solid rgba(255,255,255,0.05)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <span style={{ fontSize: '16px' }}>🌍</span>
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'white' }}>
                Represent Your Country
              </span>
            </div>

            {/* Search */}
            <div style={{ padding: '10px' }}>
              <input
                type="text"
                placeholder="Search countries..."
                value={countrySearch}
                onChange={(e) => setCountrySearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '10px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  background: 'rgba(255,255,255,0.05)',
                  color: 'white',
                  fontSize: '13px',
                  outline: 'none',
                }}
                autoFocus
              />
            </div>

            {/* Country list */}
            <div style={{ maxHeight: '240px', overflowY: 'auto', padding: '0 6px 6px' }}>
              {filteredCountries.map((country) => (
                <button
                  key={country.code}
                  onClick={() => handleCountrySelect(country)}
                  disabled={loading}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 12px',
                    borderRadius: '10px',
                    border: 'none',
                    background: user?.countryCode === country.code ? 'rgba(20,241,149,0.15)' : 'transparent',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.5 : 1,
                    textAlign: 'left',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    if (user?.countryCode !== country.code) {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = user?.countryCode === country.code 
                      ? 'rgba(20,241,149,0.15)' 
                      : 'transparent';
                  }}
                >
                  <FlagImage code={country.code} size={24} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', color: 'white', fontWeight: 500 }}>{country.name}</div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
                      {country.memberCount} {country.memberCount === 1 ? 'builder' : 'builders'}
                    </div>
                  </div>
                  {user?.countryCode === country.code && (
                    <svg style={{ width: '16px', height: '16px', color: '#14F195' }} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                    </svg>
                  )}
                </button>
              ))}
              {filteredCountries.length === 0 && (
                <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>
                  No countries found
                </div>
              )}
            </div>

            {/* Remove country option */}
            {userCountry && (
              <>
                <div style={{ height: '1px', background: 'rgba(255,255,255,0.05)', margin: '0 10px' }} />
                <div style={{ padding: '8px' }}>
                  <button
                    onClick={handleCountryRemove}
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '10px',
                      border: 'none',
                      background: 'transparent',
                      color: '#ef4444',
                      fontSize: '13px',
                      fontWeight: 500,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.5 : 1,
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(239,68,68,0.1)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    Stop representing {userCountry.name}
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
